import axios from 'axios';
import { handleSessionExpired } from './auth';

// ── Configuration ───────────────────────────────────────────────────────────
const CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID || '';
const REDIRECT_URI = typeof window !== 'undefined' ? window.location.origin : '';
// offline_access is REQUIRED to receive a refresh_token from Azure AD
const SCOPES = 'Mail.Send Mail.Read Mail.ReadWrite User.Read offline_access';

// localStorage keys
export const MS_TOKEN_KEY = 'ms_access_token';
export const MS_REFRESH_KEY = 'ms_refresh_token';
export const MS_EXPIRES_KEY = 'ms_token_expires_at';
const PKCE_VERIFIER_KEY = 'ms_pkce_verifier';

// Refresh the token this many ms before it actually expires (5 min safety buffer)
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

// ── PKCE helpers ────────────────────────────────────────────────────────────

/**
 * Generate a cryptographically random code_verifier (43-128 chars, RFC 7636).
 * Uses the Web Crypto API.
 */
function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64urlEncode(array);
}

/**
 * Compute the code_challenge from a code_verifier using S256 method:
 *   code_challenge = base64url(SHA-256(code_verifier))
 */
async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64urlEncode(new Uint8Array(digest));
}

/**
 * Base64URL-encode a byte array (no padding, + → -, / → _).
 */
function base64urlEncode(bytes) {
  const str = String.fromCharCode(...bytes);
  const base64 = btoa(str);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ── Token storage helpers ───────────────────────────────────────────────────

function storeTokens(accessToken, refreshToken, expiresIn) {
  localStorage.setItem(MS_TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(MS_REFRESH_KEY, refreshToken);
  }
  const expiresAt = Date.now() + expiresIn * 1000;
  localStorage.setItem(MS_EXPIRES_KEY, String(expiresAt));
}

export function clearMicrosoftTokens() {
  localStorage.removeItem(MS_TOKEN_KEY);
  localStorage.removeItem(MS_REFRESH_KEY);
  localStorage.removeItem(MS_EXPIRES_KEY);
}

/**
 * Returns true if the stored access token is missing or about to expire.
 */
function isTokenExpiredOrExpiring() {
  const expiresAt = parseInt(localStorage.getItem(MS_EXPIRES_KEY) || '0', 10);
  return Date.now() >= expiresAt - EXPIRY_BUFFER_MS;
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Start the Microsoft OAuth 2.0 Authorization Code + PKCE flow.
 * Generates a PKCE verifier/challenge, stores the verifier, and redirects
 * the browser to Microsoft's authorize endpoint.
 */
export async function startMicrosoftAuth() {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
  localStorage.setItem('pending_provider', 'microsoft');

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    redirect_uri: REDIRECT_URI,
    scope: SCOPES,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    response_mode: 'query',
  });

  window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;
}

/**
 * Handle the OAuth redirect callback: exchange the authorization code for
 * access + refresh tokens via the backend proxy (keeps client_secret server-side).
 *
 * @param {string} code — the authorization code from the redirect URL
 * @returns {Promise<string>} the access token
 */
export async function handleMicrosoftCallback(code) {
  const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);

  if (!codeVerifier) {
    throw new Error('PKCE verifier not found. Please sign in again.');
  }

  const { data } = await axios.post('/api/auth/microsoft/exchange', {
    code,
    codeVerifier,
    redirectUri: REDIRECT_URI,
  });

  storeTokens(data.access_token, data.refresh_token, data.expires_in);
  localStorage.setItem('active_provider', 'microsoft');

  return data.access_token;
}

/**
 * Use the stored refresh_token to obtain a fresh access_token.
 * On failure, clears the session and triggers auto-logout.
 *
 * @returns {Promise<string|null>} the new access token, or null if refresh failed
 */
export async function refreshAccessToken() {
  const refreshToken = localStorage.getItem(MS_REFRESH_KEY);
  if (!refreshToken) {
    handleSessionExpired('Your Microsoft session has expired. Please sign in again.');
    return null;
  }

  try {
    const { data } = await axios.post('/api/auth/microsoft/refresh', {
      refreshToken,
    });

    storeTokens(data.access_token, data.refresh_token, data.expires_in);
    return data.access_token;
  } catch (error) {
    console.error('Microsoft token refresh failed:', error.response?.data || error.message);
    handleSessionExpired('Your Microsoft session has expired. Please sign in again.');
    return null;
  }
}

/**
 * Return a valid (non-expired) Microsoft access token.
 * If the current token is about to expire, silently refreshes it first.
 * If no token or refresh fails, returns null (caller should prompt sign-in).
 *
 * @returns {Promise<string|null>}
 */
export async function getValidAccessToken() {
  const accessToken = localStorage.getItem(MS_TOKEN_KEY);
  if (!accessToken) return null;

  if (isTokenExpiredOrExpiring()) {
    return await refreshAccessToken();
  }

  return accessToken;
}

/**
 * Synchronous check — returns the stored token if it exists (may be expired).
 * Use getValidAccessToken() for the async version that auto-refreshes.
 */
export function getStoredAccessToken() {
  return localStorage.getItem(MS_TOKEN_KEY) || '';
}
